import { useCallback, useMemo, useState } from 'react';

import { DocumentType } from '@prisma/client';

import { useQuery } from '@redwoodjs/web';

import { showSuccessToast } from 'src/components/Toasts/toast';
import { useAdobeSignDocument } from 'src/features/adobeSign/hooks/useAdobeSignDocument';
import {
    getMerchantSepaAgreementColumns,
    getPartnerSepaAgreementColumns,
} from 'src/features/finance/sepaDirectDebits/components/SepaAgreementTables/SepaAgreementTables.utils';

import { useDeleteSepaAgreement } from 'src/features/finance/sepaDirectDebits/hooks/useDeleteSepaAgreement';
import { useRemindSepaAgreement } from 'src/features/finance/sepaDirectDebits/hooks/useRemindSepaAgreement';
import { GET_SEPA_DD_MANDATE_AGREEMENTS } from 'src/graphql/queries/getAgreementDocuments.query';
import {
    FilterOptionsType,
    SEPA_MANDATE_AGREEMENT_TABLE_TYPE,
    sepaMandateAgreementFilterOptions,
} from 'src/utils/tableUtils';
import { onError } from 'src/utils/utils';

function useSepaAgreement() {
    const [merchantFilterOptions, setMerchantFilterOptions] = useState<FilterOptionsType>({});
    const [partnerFilterOptions, setPartnerFilterOptions] = useState<FilterOptionsType>({});
    const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState<boolean>(false);
    const {
        getAdobeSignDocumentPdf,
        adobeDocumentPdf,
        isPDFModalOpen,
        setIsPDFModalOpen,
        pdfFile,
        loadingAgreementDocumentPdf,
    } = useAdobeSignDocument();

    const {
        data: merchantData,
        loading: loadingMerchantData,
        refetch: refetchMerchantData,
    } = useQuery(GET_SEPA_DD_MANDATE_AGREEMENTS, {
        fetchPolicy: 'network-only',
        variables: {
            filters: {
                entityFilter: SEPA_MANDATE_AGREEMENT_TABLE_TYPE.MERCHANT,
            },
        },
        onError,
        onCompleted(data) {
            const options = sepaMandateAgreementFilterOptions(
                data?.getSepaMandates?.data ?? [],
                SEPA_MANDATE_AGREEMENT_TABLE_TYPE.MERCHANT
            );
            setMerchantFilterOptions(options);
        },
    });

    const {
        data: partnerData,
        loading: loadingPartnerData,
        refetch: refetchPartnerData,
    } = useQuery(GET_SEPA_DD_MANDATE_AGREEMENTS, {
        fetchPolicy: 'network-only',
        variables: {
            filters: {
                entityFilter: SEPA_MANDATE_AGREEMENT_TABLE_TYPE.PARTNER,
            },
        },
        onError,
        onCompleted(data) {
            const options = sepaMandateAgreementFilterOptions(
                data?.getSepaMandates?.data ?? [],
                SEPA_MANDATE_AGREEMENT_TABLE_TYPE.PARTNER
            );
            setPartnerFilterOptions(options);
        },
    });

    const handleRefetchMerchantData = useCallback(() => {
        refetchMerchantData({
            filters: {
                entityFilter: SEPA_MANDATE_AGREEMENT_TABLE_TYPE.MERCHANT,
            },
        });
    }, []);

    const handleRefetchPartnerData = useCallback(() => {
        refetchPartnerData({
            filters: {
                entityFilter: SEPA_MANDATE_AGREEMENT_TABLE_TYPE.PARTNER,
            },
        });
    }, []);

    const handleOnDeleteSepaMandate = (data) => {
        if (data?.deleteSepaMandate) {
            setIsDeleteConfirmModalOpen(false);
        }
        if (data?.deleteSepaMandate?.documentType === DocumentType.MERCHANT_SEPA_DD) {
            showSuccessToast(`Merchant SEPA Direct Debit Cancelled successfully!`);
            refetchMerchantData({
                filters: {
                    entityFilter: SEPA_MANDATE_AGREEMENT_TABLE_TYPE.MERCHANT,
                },
            });
        } else if (data?.deleteSepaMandate?.documentType === DocumentType.PARTNER_SEPA_DD) {
            showSuccessToast(`Partner SEPA Direct Debit Cancelled successfully!`);
            refetchPartnerData({
                filters: {
                    entityFilter: SEPA_MANDATE_AGREEMENT_TABLE_TYPE.PARTNER,
                },
            });
        }
    };

    const { handleDeleteSepaMandate, setIdToDelete } = useDeleteSepaAgreement({
        handleOnDeleteSepaMandate,
    });

    const {
        handleRemindRecord,
        isRemindConfirmModalOpen,
        remindData,
        setIdToRemind,
        setIsRemindConfirmModalOpen,
    } = useRemindSepaAgreement({
        merchantData,
        partnerData,
    });

    const handleViewRecord = useCallback(
        (rowData) => {
            const { documentId, agreementId } = rowData;

            getAdobeSignDocumentPdf({ documentId, agreementId });
        },
        [getAdobeSignDocumentPdf]
    );

    const merchantSepaAgreementColumns = useMemo(
        () =>
            getMerchantSepaAgreementColumns({
                merchantFilterOptions,
                handleViewRecord,
                setIsRemindConfirmModalOpen,
                setIdToRemind,
                setIdToDelete,
                setIsDeleteConfirmModalOpen,
            }),
        [handleViewRecord, merchantFilterOptions]
    );

    const partnerSepaAgreementColumns = useMemo(
        () =>
            getPartnerSepaAgreementColumns({
                partnerFilterOptions,
                handleViewRecord,
                setIsRemindConfirmModalOpen,
                setIdToRemind,
                setIdToDelete,
                setIsDeleteConfirmModalOpen,
            }),
        [handleViewRecord, partnerFilterOptions]
    );

    return {
        merchant: {
            merchantData: merchantData?.getSepaMandates?.data,
            loadingMerchantData,
            merchantSepaAgreementColumns,
            refetchMerchantData,
            handleRefetchMerchantData,
        },
        partner: {
            partnerData: partnerData?.getSepaMandates?.data,
            loadingPartnerData,
            partnerSepaAgreementColumns,
            refetchPartnerData,
            handleRefetchPartnerData,
        },
        adobeDocumentPdf,
        setIsPDFModalOpen,
        isPDFModalOpen,
        pdfFile,
        loadingAgreementDocumentPdf,
        isRemindConfirmModalOpen,
        setIsRemindConfirmModalOpen,
        handleRemindRecord,
        remindData,
        handleDeleteSepaMandate,
        isDeleteConfirmModalOpen,
        setIsDeleteConfirmModalOpen,
    };
}

export default useSepaAgreement;
